import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import { PointLedgersModule } from './modules/PointLedgersModule';
import { OrdersModule } from './modules/OrdersModule';
import { useMembershipStore } from '@/stores/membershipStore';

export default function PointTransactionsPage() {
  const {
    openMembershipModal,
    checkInStatus,
    isCheckInLoading,
    fetchCheckInStatus: loadCheckInStatus,
    doCheckIn,
  } = useMembershipStore();

  // 主页签：ledgers (积分流水) | orders (订单记录)
  const [activeMainTab, setActiveMainTab] = useState<'ledgers' | 'orders'>('ledgers');
  const [toastMsg, setToastMsg] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  useEffect(() => {
    void loadCheckInStatus();
  }, [loadCheckInStatus, refreshTrigger]);

  const handleHeaderCheckIn = async () => {
    if (checkInStatus?.checked_in) {
      showToast('今日已签到过，明天再来吧！');
      return;
    }

    try {
      const res = await doCheckIn();
      if (res) {
        const gained = res.award_amount ?? 10;
        showToast(`签到成功！获得 +${gained} 积分`);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (err: any) {
      showToast(err?.message || '签到失败，请稍后重试');
    }
  };

  return (
    <AccountLayout
      activePath="/point-transactions"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: activeMainTab === 'ledgers' ? '积分流水' : '订单记录' }]}
    >
      {/* Toast 提示 */}
      {toastMsg && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2 bg-slate-900 text-white text-xs font-semibold rounded-full shadow-2xl animate-[fadeIn_0.2s_ease-out] flex items-center gap-2">
          <i className="ri-checkbox-circle-fill text-emerald-400 text-sm"></i>
          <span>{toastMsg}</span>
        </div>
      )}

      {/* 页头 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2.5">
            {activeMainTab === 'ledgers' ? '积分流水' : '订单记录'}
            <span className="text-xs font-normal px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200/60">
              {activeMainTab === 'ledgers' ? '收支明细' : '订单中心'}
            </span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {activeMainTab === 'ledgers'
              ? '查看您的积分赠送、充值及消费明细记录'
              : '查看和管理您的会员订阅及积分充值订单'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveMainTab(activeMainTab === 'ledgers' ? 'orders' : 'ledgers')}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs border border-slate-200 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className={activeMainTab === 'ledgers' ? 'ri-file-text-line text-sm' : 'ri-coins-line text-sm'}></i>
            {activeMainTab === 'ledgers' ? '订单记录' : '积分流水'}
          </button>

          {activeMainTab === 'ledgers' && (
            <button
              onClick={handleHeaderCheckIn}
              disabled={isCheckInLoading || Boolean(checkInStatus?.checked_in)}
              className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl font-semibold text-xs border transition-all shadow-2xs cursor-pointer disabled:opacity-75 ${checkInStatus?.checked_in
                ? 'bg-slate-100 text-slate-500 border-slate-200 cursor-not-allowed'
                : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200/80'
                }`}
            >
              <i className={checkInStatus?.checked_in ? 'ri-checkbox-circle-fill text-emerald-500 text-sm' : 'ri-calendar-check-line text-sm'}></i>
              {isCheckInLoading
                ? '签到中...'
                : checkInStatus?.checked_in
                  ? `今日已签到 (连续 ${checkInStatus.cycle_day || 1} 天)`
                  : checkInStatus?.next_award_amount
                    ? `签到领 +${checkInStatus.next_award_amount} 积分`
                    : '每日签到领积分'}
            </button>
          )}

          <button
            onClick={() =>
              openMembershipModal({
                defaultTab: activeMainTab === 'ledgers' ? 'points' : 'membership',
              })
            }
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white font-semibold text-xs transition-all shadow-md shadow-amber-400/20 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-vip-crown-line text-sm"></i>
            {activeMainTab === 'ledgers' ? '充值 / 获取积分' : '购买会员 / 积分'}
          </button>
        </div>
      </div>

      {activeMainTab === 'ledgers' ? (
        <PointLedgersModule refreshTrigger={refreshTrigger} />
      ) : (
        <OrdersModule />
      )}
    </AccountLayout>
  );
}
